import {
  collection,
  doc,
  DocumentReference,
  Firestore
} from 'firebase/firestore';
import { NovelStorageLayout } from '@/lib/types';

/**
 * Result of a path resolution.
 * Contains the document reference and optionally the field path within that document.
 */
export interface ResolutionResult {
  ref: DocumentReference;
  fieldPath?: string;
}

/**
 * Utility to resolve Firestore paths and field locations based on NovelStorageLayout.
 * Frontend equivalent of the backend StorageResolver, using the Client SDK.
 */
export class StorageResolver {
  /**
   * Initializes the StorageResolver with a Firestore instance.
   * @param db Firestore database instance.
   */
  constructor(private db: Firestore) {}

  /**
   * Resolves the root Novel document reference.
   */
  getNovelRef(userId: string, novelId: string): DocumentReference {
    return doc(this.db, 'users', userId, 'novels', novelId);
  }

  /**
   * Resolves the reference and field for an item's summary (metadata).
   */
  resolveSummary(
    userId: string,
    novelId: string,
    family: 'entities' | 'narrative',
    type: string,
    itemId: string,
    layout: NovelStorageLayout
  ): ResolutionResult {
    const metaLayout = family === 'entities' ? layout.entities : layout.narrative;
    const mode = metaLayout[type]?.meta || 'embedded';
    const metaFamily = family === 'entities' ? 'entitiesMeta' : 'narrativeMeta';

    if (mode === 'split') {
      // Split metadata lives in a separate document for that type
      const novelRef = this.getNovelRef(userId, novelId);
      const ref = doc(collection(novelRef, metaFamily), type);
      return { ref, fieldPath: `summaries.${itemId}` };
    } else {
      // Embedded metadata lives inside the novel document
      const ref = this.getNovelRef(userId, novelId);
      return { ref, fieldPath: `${metaFamily}.summaries.${type}.${itemId}` };
    }
  }

  /**
   * Resolves the reference and field for all summaries of a given type.
   */
  resolveAllSummaries(
    userId: string,
    novelId: string,
    family: 'entities' | 'narrative',
    type: string,
    layout: NovelStorageLayout
  ): ResolutionResult {
    const metaLayout = family === 'entities' ? layout.entities : layout.narrative;
    const mode = metaLayout[type]?.meta || 'embedded';
    const metaFamily = family === 'entities' ? 'entitiesMeta' : 'narrativeMeta';

    if (mode === 'split') {
      const novelRef = this.getNovelRef(userId, novelId);
      const ref = doc(collection(novelRef, metaFamily), type);
      return { ref, fieldPath: 'summaries' };
    } else {
      const ref = this.getNovelRef(userId, novelId);
      return { ref, fieldPath: `${metaFamily}.summaries.${type}` };
    }
  }

  /**
   * Resolves the reference and field for a full item document.
   */
  resolveItem(
    userId: string,
    novelId: string,
    family: 'entities' | 'narrative',
    type: string,
    itemId: string,
    layout: NovelStorageLayout
  ): ResolutionResult {
    const itemLayout = family === 'entities' ? layout.entities : layout.narrative;
    const mode = itemLayout[type]?.items || 'embedded';

    if (mode === 'split') {
      // Split items live in their own document at the novel level: /novels/{id}/{type}/{itemId}
      const novelRef = this.getNovelRef(userId, novelId);
      const ref = doc(collection(novelRef, type), itemId);
      return { ref };
    } else {
      // Embedded items live inside a bucket document: /novels/{id}/{family}/{type}
      const novelRef = this.getNovelRef(userId, novelId);
      const ref = doc(collection(novelRef, family), type);
      return { ref, fieldPath: `entityList.${itemId}` };
    }
  }

  /**
   * Resolves the bucket document for embedded items.
   */
  getBucketRef(
    userId: string,
    novelId: string,
    family: 'entities' | 'narrative',
    type: string
  ): DocumentReference {
    const novelRef = this.getNovelRef(userId, novelId);
    return doc(collection(novelRef, family), type);
  }
}
